import { ConversionCard, FounderAdminCard, UserProfileDetails } from "@/components/admin/GrowthCards";
import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Users, Newspaper, Radio, Gift, Trophy, RefreshCw, Plus, Trash2,
  Sparkles, Power, ShieldCheck, Check, LayoutDashboard, Settings, Search,
  ChevronLeft, ChevronRight, X, Flame, Coins, BarChart3, Eye, Award, ArrowUpDown,
  TrendingUp, UserPlus, Clock, Bell, Send, KeyRound, Megaphone, Download,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ManualArticleTab } from "@/components/admin/ManualArticleTab";
import { EngagementTab } from "@/components/admin/EngagementTab";
import { useSettings } from "@/context/SettingsContext";
import { displayCat, CATEGORY_COLOR } from "@/lib/readoo";
import {
  adminStats, adminUsers, adminUserStats, adminDeleteUser, adminRedemptions, adminFulfill,
  adminGenerateHoroscope, adminSetGamification, adminSetLogin, adminConsentsCsvUrl, adminGetAskWeb, adminSetAskWeb, adminGetBrevo, adminSetBrevoKey, adminTestBrevo, adminGetGeneration, adminSetGeneration, getSources, createSource, deleteSource, updateSourceCategory,
  syncSource, syncAllSources, adminArticles, adminDeleteArticle, adminBulkDeleteArticles, adminCleanupDrafts, getCategories,
  adminSetRole, adminSignupSources, adminRewards, adminCreateReward, adminUpdateReward, adminDeleteReward, adminTimeseries,
  adminGetCategoryRefresh, adminSetCategoryRefresh,
  adminPushOverview, adminCreateRecurring, adminUpdateRecurring, adminDeleteRecurring, adminPushSend, adminDeleteScheduled,
  adminSetSoftAsk, adminGetLevels, adminSetLevels, adminGetLlm, adminSetLlm,
  adminAdblocks, adminCreateAdblock, adminUpdateAdblock, adminDeleteAdblock,
} from "@/lib/api";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "utenti", label: "Utenti", icon: Users },
  { id: "articoli", label: "Articoli", icon: Newspaper },
  { id: "scrivi", label: "Scrivi da link", icon: Sparkles },
  { id: "engagement", label: "Engagement", icon: Trophy },
  { id: "fonti", label: "Fonti RSS", icon: Radio, superadmin: true },
  { id: "premi", label: "Premi", icon: Award },
  { id: "livelli", label: "Livelli", icon: Trophy },
  { id: "riscatti", label: "Riscatti", icon: Gift },
  { id: "notifiche", label: "Notifiche", icon: Bell },
  { id: "pubblicita", label: "Pubblicità", icon: Megaphone, superadmin: true },
  { id: "impostazioni", label: "Impostazioni", icon: Settings, superadmin: true },
];

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };
const fmtDay = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }); } catch { return s; } };

function Card({ children, className = "" }) {
  return <div className={`bg-surfaceSecondary border border-hair rounded-2xl p-5 sm:p-6 ${className}`}>{children}</div>;
}

function PageSizeSelect({ value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(Number(e.target.value))}
      className="bg-surface border border-hair rounded-full px-4 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
      <option value={10}>10 / pagina</option>
      <option value={20}>20 / pagina</option>
      <option value={50}>50 / pagina</option>
    </select>
  );
}

function Pager({ page, totalPages, total, onPrev, onNext, prefix }) {
  return (
    <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-hair">
      <p className="text-xs text-subtle" data-testid={`${prefix}-total`}>{total} risultati · pagina {page} di {totalPages}</p>
      <div className="flex items-center gap-2">
        <button onClick={onPrev} disabled={page <= 1} data-testid={`${prefix}-prev`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-hair text-xs text-onSurfaceSecondary hover:text-brand hover:border-brand/50 disabled:opacity-40 transition-colors"><ChevronLeft size={14} /> Prec</button>
        <button onClick={onNext} disabled={page >= totalPages} data-testid={`${prefix}-next`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-hair text-xs text-onSurfaceSecondary hover:text-brand hover:border-brand/50 disabled:opacity-40 transition-colors">Succ <ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-surfaceSecondary border border-hair rounded-xl p-4 flex items-center gap-3" data-testid={`stat-${label}`}>
      <span className="w-10 h-10 rounded-lg bg-brandTertiary text-brand flex items-center justify-center shrink-0"><Icon size={18} /></span>
      <div>
        <p className="font-serif text-2xl font-semibold text-onSurface leading-none">{value ?? "—"}</p>
        <p className="text-xs text-subtle mt-1">{label}</p>
      </div>
    </div>
  );
}

function WeeklyChart() {
  const [data, setData] = useState(null);
  useEffect(() => { adminTimeseries(7).then(setData).catch(() => {}); }, []);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div>;
  const max = Math.max(1, ...data.reads, ...data.signups);
  const totalReads = data.reads.reduce((a, b) => a + b, 0);
  const totalSignups = data.signups.reduce((a, b) => a + b, 0);
  return (
    <div data-testid="weekly-chart">
      <div className="flex items-center gap-5 mb-5 text-xs">
        <span className="inline-flex items-center gap-1.5 text-onSurfaceSecondary"><span className="w-3 h-3 rounded-sm bg-brand" /> Letture ({totalReads})</span>
        <span className="inline-flex items-center gap-1.5 text-onSurfaceSecondary"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Iscritti ({totalSignups})</span>
      </div>
      <div className="flex items-end justify-between gap-2 h-44">
        {data.labels.map((d, i) => (
          <div key={d} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end" data-testid={`chart-col-${i}`}>
            <div className="flex items-end gap-1 w-full justify-center h-full">
              <div className="w-1/2 max-w-[22px] bg-brand rounded-t transition-all" style={{ height: `${(data.reads[i] / max) * 100}%` }} title={`${data.reads[i]} letture`} />
              <div className="w-1/2 max-w-[22px] bg-emerald-500 rounded-t transition-all" style={{ height: `${(data.signups[i] / max) * 100}%` }} title={`${data.signups[i]} iscritti`} />
            </div>
            <span className="text-[10px] text-subtle whitespace-nowrap">{fmtDay(d)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupSourcesCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); adminSignupSources(days).then(setData).catch(() => setData({ campaigns: [] })); }, [days]);
  const share = data?.all_signups_period ? Math.round((data.period / data.all_signups_period) * 100) : 0;
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-onSurface"><UserPlus size={18} className="text-brand" /> Iscrizioni da /vantaggi</h2>
        <select data-testid="signup-sources-days" value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="bg-surface border border-hair rounded-full px-4 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
          <option value={7}>Ultimi 7 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
          <option value={90}>Ultimi 90 giorni</option>
        </select>
      </div>
      {!data ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-brand" size={20} /></div> : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div data-testid="signup-sources-period"><p className="font-serif text-2xl font-semibold text-onSurface">{data.period ?? 0}</p><p className="text-xs text-subtle">nel periodo</p></div>
            <div data-testid="signup-sources-share"><p className="font-serif text-2xl font-semibold text-onSurface">{share}%</p><p className="text-xs text-subtle">di {data.all_signups_period ?? 0} iscrizioni totali</p></div>
            <div data-testid="signup-sources-total"><p className="font-serif text-2xl font-semibold text-onSurface">{data.total ?? 0}</p><p className="text-xs text-subtle">da sempre</p></div>
          </div>
          <div className="mt-5 border-t border-hair pt-4">
            <p className="text-xs font-mono uppercase tracking-wider text-subtle mb-2">Per campagna (utm_source / utm_campaign)</p>
            {data.campaigns.length === 0 ? <p data-testid="signup-sources-empty" className="text-sm text-subtle">Nessuna iscrizione nel periodo.</p> : (
              <ul data-testid="signup-sources-campaigns" className="divide-y divide-hair">
                {data.campaigns.map((c, i) => (
                  <li key={`${c.utm_source}|${c.utm_campaign}`} data-testid={`signup-sources-row-${i}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="text-onSurface truncate">{c.utm_source || "diretto / nessuna"}{c.utm_campaign ? <span className="text-subtle"> · {c.utm_campaign}</span> : null}</span>
                    <span className="font-semibold text-onSurface">{c.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function DashboardTab({ stats }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={Users} label="Utenti" value={stats?.users} />
        <StatCard icon={Newspaper} label="Articoli" value={stats?.articles} />
        <StatCard icon={Radio} label="Fonti" value={stats?.sources} />
        <StatCard icon={Gift} label="Riscatti pendenti" value={stats?.pending_redemptions} />
      </div>
      <Card>
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-onSurface mb-4"><TrendingUp size={18} className="text-brand" /> Andamento ultimi 7 giorni</h2>
        <WeeklyChart />
      </Card>
      <SignupSourcesCard />
      <ConversionCard />
      <FounderAdminCard />
    </div>
  );
}

/* ---------------- Utenti ---------------- */
function UserStatsModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { adminUserStats(userId).then(setData).catch(() => toast.error("Errore nel caricare le statistiche")); }, [userId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-testid="user-stats-modal">
      <div className="w-full max-w-lg bg-surfaceSecondary border border-hair rounded-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="font-serif text-xl font-semibold text-onSurface">{data?.user?.name || "Utente"}</h3>
            <p className="text-xs text-subtle">{data?.user?.email}</p>
          </div>
          <button onClick={onClose} data-testid="close-user-stats" className="w-8 h-8 flex items-center justify-center rounded-full border border-hair text-subtle hover:text-brand"><X size={16} /></button>
        </div>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div>
        ) : (
          <>
            <UserProfileDetails p={data.profile} />
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-surface border border-hair rounded-xl p-3 text-center"><Coins className="text-brand mx-auto mb-1" size={16} /><p className="font-serif text-lg font-semibold text-onSurface">{data.user.points}</p><p className="text-[11px] text-subtle">Readoo</p></div>
              <div className="bg-surface border border-hair rounded-xl p-3 text-center"><BarChart3 className="text-brand mx-auto mb-1" size={16} /><p className="font-serif text-lg font-semibold text-onSurface">Lv {data.user.level}</p><p className="text-[11px] text-subtle">Livello</p></div>
              <div className="bg-surface border border-hair rounded-xl p-3 text-center"><Flame className="text-brand mx-auto mb-1" size={16} /><p className="font-serif text-lg font-semibold text-onSurface">{data.user.streak}</p><p className="text-[11px] text-subtle">Streak</p></div>
              <div className="bg-surface border border-hair rounded-xl p-3 text-center"><Newspaper className="text-brand mx-auto mb-1" size={16} /><p className="font-serif text-lg font-semibold text-onSurface">{data.articles_read}</p><p className="text-[11px] text-subtle">Letti</p></div>
              <div className="bg-surface border border-hair rounded-xl p-3 text-center"><Gift className="text-brand mx-auto mb-1" size={16} /><p className="font-serif text-lg font-semibold text-onSurface">{data.redemptions}</p><p className="text-[11px] text-subtle">Riscatti</p></div>
              <div className="bg-surface border border-hair rounded-xl p-3 text-center"><ShieldCheck className="text-brand mx-auto mb-1" size={16} /><p className="font-serif text-sm font-semibold text-onSurface capitalize">{data.user.role}</p><p className="text-[11px] text-subtle">Ruolo</p></div>
            </div>
            <p className="text-xs font-mono uppercase tracking-wider text-subtle mb-2">Storico Readoo (ultimi 20)</p>
            <div className="divide-y divide-hair/50">
              {data.points_history.length === 0 && <p className="text-sm text-subtle py-2">Nessun movimento Readoo.</p>}
              {data.points_history.map((h) => (
                <div key={h.entry_id} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-onSurfaceSecondary truncate">{h.reason}</span>
                  <span className={`font-mono font-semibold ${h.delta >= 0 ? "text-ok" : "text-bad"}`}>{h.delta >= 0 ? "+" : ""}{h.delta}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function YesNo({ v, testid }) {
  if (v === null || v === undefined) return <span data-testid={testid} className="text-xs text-subtle">—</span>;
  return <span data-testid={testid} className={`font-mono text-xs font-bold ${v ? "text-emerald-500" : "text-subtle"}`}>{v ? "Sì" : "No"}</span>;
}

function UsersTab({ users, reload }) {
  const { user: me } = useAuth();
  const [search, setSearch] = useState("");
  const [statsUser, setStatsUser] = useState(null);
  const [busy, setBusy] = useState("");
  const filtered = users.filter((u) => {
    const t = search.trim().toLowerCase();
    return !t || (u.name || "").toLowerCase().includes(t) || (u.email || "").toLowerCase().includes(t);
  });
  const del = async (u) => {
    if (!window.confirm(`Eliminare definitivamente ${u.email}? Verranno rimossi anche i suoi dati gamification.`)) return;
    setBusy(u.user_id);
    try { await adminDeleteUser(u.user_id); toast.success("Utente eliminato"); reload(); }
    catch (e) { toast.error(e.message || "Errore"); }
    finally { setBusy(""); }
  };
  const exportConsents = async () => {
    try {
      const r = await fetch(adminConsentsCsvUrl(), { headers: { Authorization: `Bearer ${localStorage.getItem("readoo_token")}` } });
      if (!r.ok) throw new Error("Export non riuscito");
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url; a.download = "readoo-consensi.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };
  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "user" : "admin";
    if (!window.confirm(next === "admin" ? `Promuovere ${u.email} ad admin?` : `Declassare ${u.email} a utente?`)) return;
    setBusy(u.user_id);
    try { await adminSetRole(u.user_id, next); toast.success(next === "admin" ? "Promosso ad admin" : "Declassato a utente"); reload(); }
    catch (e) { toast.error(e.message || "Errore"); }
    finally { setBusy(""); }
  };
  return (
    <Card>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
      <div className="relative max-w-sm w-full">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle" />
        <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome o email"
          className="w-full bg-surface border border-hair rounded-full pl-9 pr-4 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
      </div>
        <button onClick={exportConsents} data-testid="users-export-consents" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-hair text-xs font-semibold text-onSurfaceSecondary hover:text-brand hover:border-brand/50 transition-colors">
          <Download size={14} /> Esporta consensi (CSV)
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-mono uppercase tracking-wider text-subtle border-b border-hair">
              <th className="py-2 pr-3 font-medium">Nome</th>
              <th className="py-2 pr-3 font-medium">Email</th>
              <th className="py-2 pr-3 font-medium">Ruolo</th>
              <th className="py-2 pr-3 font-medium">Readoo</th>
              <th className="py-2 pr-3 font-medium" title="Newsletter e promozioni (profilazione)">Newsletter</th>
              <th className="py-2 pr-3 font-medium" title="Cessione dati ai partner">Partner</th>
              <th className="py-2 pr-3 font-medium">Founder</th>
              <th className="py-2 pr-3 font-medium">Interessi</th>
              <th className="py-2 pr-3 font-medium">Salvati</th>
              <th className="py-2 pr-3 font-medium">Ultimo accesso</th>
              <th className="py-2 pr-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hair/50">
            {filtered.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <td className="py-2 pr-3 text-onSurface">{u.name || "—"}</td>
                <td className="py-2 pr-3 text-onSurfaceSecondary">{u.email}</td>
                <td className="py-2 pr-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${u.role === "superadmin" ? "bg-brand text-onBrand" : u.role === "admin" ? "bg-brandTertiary text-brand" : "bg-surfaceTertiary text-onSurfaceSecondary"}`}>
                    {(u.role === "admin" || u.role === "superadmin") && <Trophy size={11} />} {u.role}
                  </span>
                </td>
                <td className="py-2 pr-3 font-mono text-onSurfaceSecondary">{u.points ?? 0}</td>
                <td className="py-2 pr-3"><YesNo v={u.consent_newsletter} testid={`user-consent-newsletter-${u.user_id}`} /></td>
                <td className="py-2 pr-3"><YesNo v={u.consent_partners} testid={`user-consent-partners-${u.user_id}`} /></td>
                <td className="py-2 pr-3"><YesNo v={u.role === "user" ? u.founder : null} testid={`user-founder-${u.user_id}`} /></td>
                <td className="py-2 pr-3 font-mono text-onSurfaceSecondary" data-testid={`user-interests-${u.user_id}`}>{u.interests_count ?? 0}</td>
                <td className="py-2 pr-3 font-mono text-onSurfaceSecondary" data-testid={`user-saved-${u.user_id}`}>{u.saved_count ?? 0}</td>
                <td className="py-2 pr-3 text-xs text-subtle whitespace-nowrap" data-testid={`user-lastseen-${u.user_id}`}>{u.last_seen_at ? new Date(u.last_seen_at).toLocaleDateString("it-IT") : "—"}</td>
                <td className="py-2 pr-0">
                  <div className="flex items-center justify-end gap-1.5">
                    {u.role !== "superadmin" && (
                      <button onClick={() => toggleRole(u)} disabled={busy === u.user_id || u.user_id === me?.user_id} data-testid={`user-role-${u.user_id}`}
                        title={u.user_id === me?.user_id ? "Non puoi cambiare il tuo ruolo" : ""}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-hair text-xs text-onSurfaceSecondary hover:text-brand hover:border-brand/50 disabled:opacity-40 transition-colors">
                        {u.role === "admin" ? <ArrowUpDown size={13} /> : <UserPlus size={13} />} {u.role === "admin" ? "Rendi utente" : "Rendi admin"}
                      </button>
                    )}
                    <button onClick={() => setStatsUser(u.user_id)} data-testid={`user-stats-${u.user_id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-hair text-xs text-onSurfaceSecondary hover:text-brand hover:border-brand/50 transition-colors"><Eye size={13} /> Scheda</button>
                    <button onClick={() => del(u)} disabled={busy === u.user_id} data-testid={`user-delete-${u.user_id}`}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-hair text-bad hover:border-bad/50 transition-colors">
                      {busy === u.user_id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-sm text-subtle py-4">Nessun utente trovato.</p>}
      </div>
      {statsUser && <UserStatsModal userId={statsUser} onClose={() => setStatsUser(null)} />}
    </Card>
  );
}

/* ---------------- Articoli ---------------- */
function ArticlesTab() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ articles: [], total: 0 });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { getCategories().then((r) => setCats(r.categories || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminArticles({ q, category, status, page, page_size: pageSize })); setSelected(new Set()); }
    catch (e) { toast.error(e.message || "Errore"); }
    finally { setLoading(false); }
  }, [q, category, status, page, pageSize]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, category, status, pageSize]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const del = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    setBusy(id);
    try { await adminDeleteArticle(id); toast.success("Articolo eliminato"); await load(); }
    catch (e) { toast.error(e.message || "Errore"); }
    finally { setBusy(""); }
  };
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const pageIds = data.articles.map((a) => a.article_id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allOnPageSelected) pageIds.forEach((id) => n.delete(id));
    else pageIds.forEach((id) => n.add(id));
    return n;
  });
  const bulkDel = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} articoli selezionati?`)) return;
    setBulkBusy(true);
    try {
      const r = await adminBulkDeleteArticles(ids);
      toast.success(`${r.deleted ?? ids.length} articoli eliminati`);
      await load();
    } catch (e) { toast.error(e.message || "Errore"); }
    finally { setBulkBusy(false); }
  };
  const [cleanBusy, setCleanBusy] = useState(false);
  const cleanupDrafts = async () => {
    if (!window.confirm("Eliminare tutte le bozze non pubblicabili (bloccate o troppo vecchie per essere riscritte)?")) return;
    setCleanBusy(true);
    try {
      const r = await adminCleanupDrafts();
      toast.success(`${r.deleted ?? 0} bozze non pubblicabili rimosse`);
      await load();
    } catch (e) { toast.error(e.message || "Errore"); }
    finally { setCleanBusy(false); }
  };

  return (
    <Card>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle" />
          <input data-testid="article-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per titolo"
            className="w-full bg-surface border border-hair rounded-full pl-9 pr-4 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        </div>
        <select data-testid="article-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-surface border border-hair rounded-full px-4 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
          <option value="all">Tutte le categorie</option>
          {cats.filter((c) => c !== "Ultimissime").map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="article-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-surface border border-hair rounded-full px-4 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
          <option value="all">Tutti gli stati</option>
          <option value="published">Solo pubblicati</option>
          <option value="draft">Solo bozze</option>
        </select>
        <PageSizeSelect value={pageSize} onChange={setPageSize} testid="article-page-size" />
        <button data-testid="cleanup-drafts-btn" onClick={cleanupDrafts} disabled={cleanBusy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-hair text-onSurfaceSecondary text-sm hover:border-bad/50 hover:text-bad disabled:opacity-60 transition-colors">
          {cleanBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Pulisci bozze
        </button>
      </div>
      {selected.size > 0 && (
        <div data-testid="bulk-bar" className="flex items-center justify-between gap-3 mb-3 rounded-lg border border-brand/40 bg-brand/10 px-3 py-2">
          <span className="text-sm text-onSurface font-medium">{selected.size} selezionati</span>
          <div className="flex items-center gap-2">
            <button data-testid="bulk-clear" onClick={() => setSelected(new Set())}
              className="text-xs px-3 py-1.5 rounded-full border border-hair text-onSurfaceSecondary hover:border-onSurfaceSecondary/50 transition-colors">Deseleziona</button>
            <button data-testid="bulk-delete" onClick={bulkDel} disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-bad text-white font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity">
              {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Elimina selezionati
            </button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto min-h-[200px]">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-mono uppercase tracking-wider text-subtle border-b border-hair">
                <th className="py-2 pr-3 font-medium w-8">
                  <input data-testid="article-select-all" type="checkbox" checked={allOnPageSelected} onChange={toggleAll} className="accent-brand cursor-pointer" aria-label="Seleziona tutti" />
                </th>
                <th className="py-2 pr-3 font-medium">Titolo</th>
                <th className="py-2 pr-3 font-medium">Categoria</th>
                <th className="py-2 pr-3 font-medium">Stato</th>
                <th className="py-2 pr-3 font-medium">Data</th>
                <th className="py-2 pr-3 font-medium text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hair/50">
              {data.articles.map((a) => (
                <tr key={a.article_id} data-testid={`article-row-${a.article_id}`} className={selected.has(a.article_id) ? "bg-brand/5" : ""}>
                  <td className="py-2 pr-3">
                    <input data-testid={`article-select-${a.article_id}`} type="checkbox" checked={selected.has(a.article_id)} onChange={() => toggleOne(a.article_id)} className="accent-brand cursor-pointer" aria-label="Seleziona articolo" />
                  </td>
                  <td className="py-2 pr-3 text-onSurface max-w-[520px]"><span className="line-clamp-2">{a.title}</span></td>
                  <td className="py-2 pr-3"><span className="text-xs px-2 py-0.5 rounded-full bg-surfaceTertiary text-onSurfaceSecondary">{a.category}</span></td>
                  <td className="py-2 pr-3">
                    {a.rewritten ? (
                      <span data-testid={`article-status-${a.article_id}`} className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 font-medium whitespace-nowrap">Pubblicato</span>
                    ) : a.rewrite_blocked ? (
                      <span data-testid={`article-status-${a.article_id}`} className="text-xs px-2 py-0.5 rounded-full bg-bad/15 text-bad font-medium whitespace-nowrap">Bloccato</span>
                    ) : (
                      <span data-testid={`article-status-${a.article_id}`} className="text-xs px-2 py-0.5 rounded-full bg-warn/15 text-warn font-medium whitespace-nowrap">Bozza</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-subtle font-mono text-xs whitespace-nowrap">{fmtDate(a.published_at)}</td>
                  <td className="py-2 pr-0 text-right">
                    <button onClick={() => del(a.article_id)} disabled={busy === a.article_id} data-testid={`article-delete-${a.article_id}`}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-hair text-bad hover:border-bad/50 transition-colors">
                      {busy === a.article_id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && data.articles.length === 0 && <p className="text-sm text-subtle py-4">Nessun articolo trovato.</p>}
      </div>
      <Pager page={page} totalPages={totalPages} total={data.total} prefix="article"
        onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} />
    </Card>
  );
}

/* ---------------- Fonti ---------------- */
function CategoryRefreshPanel() {
  const [cfg, setCfg] = useState(null);
  const [vals, setVals] = useState({});
  const [arts, setArts] = useState({});
  const [recalc, setRecalc] = useState(15);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetCategoryRefresh().then((r) => {
      setCfg(r);
      setVals(Object.fromEntries(r.categories.map((c) => [c.category, c.minutes])));
      setArts(Object.fromEntries(r.categories.map((c) => [c.category, c.max_articles])));
      setRecalc(r.categories.find((c) => c.category === "Primo Piano")?.recalc_minutes ?? 15);
    }).catch(() => {});
  }, []);

  if (!cfg) return null;
  const { min, max } = cfg;
  const aMin = cfg.articles_min ?? 1;
  const aMax = cfg.articles_max ?? 20;
  const aDef = cfg.articles_default ?? 10;

  const setVal = (cat, v) => setVals((prev) => ({ ...prev, [cat]: v }));
  const setArt = (cat, v) => setArts((prev) => ({ ...prev, [cat]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const intervals = Object.entries(vals).map(([category, minutes]) => ({
        category,
        minutes: Math.max(min, Math.min(max, Number(minutes) || cfg.default)),
        max_articles: Math.max(aMin, Math.min(aMax, Number(arts[category]) || aDef)),
        ...(category === "Primo Piano" ? { recalc_minutes: Math.max(min, Math.min(max, Number(recalc) || 15)) } : {}),
      }));
      await adminSetCategoryRefresh(intervals);
      setVals(Object.fromEntries(intervals.map((i) => [i.category, i.minutes])));
      setArts(Object.fromEntries(intervals.map((i) => [i.category, i.max_articles])));
      toast.success("Frequenze aggiornate");
    } catch (e) { toast.error(e.message || "Errore"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="category-refresh-panel" className="mb-6 rounded-xl border border-hair bg-surface p-4">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <p className="text-sm font-semibold text-onSurface flex items-center gap-1.5"><Clock size={15} className="text-brand" /> Frequenza aggiornamento per categoria</p>
        <button data-testid="save-category-refresh" onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand text-onBrand text-xs font-semibold hover:bg-brandSecondary disabled:opacity-60 transition-colors">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salva frequenze
        </button>
      </div>
      <p className="text-xs text-subtle mb-3">Ogni quanti minuti sincronizzare le fonti di ciascuna categoria (da {min} a {max} min) e quanti articoli prelevare a ogni chiamata al feed (da {aMin} a {aMax}, default {aDef}). Le modifiche hanno effetto entro ~1 minuto. Calcio vale per le fonti calcistiche. Primo Piano: min vale per le fonti principali (ANSA, Corriere…), che prelevano gli articoli secondo la loro categoria; ricalc. indica ogni quanti minuti cambiano le notizie in Primo Piano.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {cfg.categories.map((c) => (
          <div key={c.category} className="flex items-center justify-between gap-2 bg-surfaceSecondary border border-hair rounded-lg px-3 py-2">
            <span className="text-sm text-onSurface truncate min-w-0">{displayCat(c.category)}</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="flex items-center gap-1">
                <input
                  type="number" min={min} max={max}
                  data-testid={`refresh-input-${c.category}`}
                  value={vals[c.category] ?? ""}
                  onChange={(e) => setVal(c.category, e.target.value)}
                  className="w-14 bg-surface border border-hair rounded px-2 py-1 text-sm text-onSurface text-right focus:outline-none focus:border-brand"
                />
                <span className="text-[11px] text-subtle">min</span>
              </span>
              {c.category !== "Primo Piano" && <span className="flex items-center gap-1">
                <input
                  type="number" min={aMin} max={aMax}
                  data-testid={`articles-input-${c.category}`}
                  value={arts[c.category] ?? ""}
                  onChange={(e) => setArt(c.category, e.target.value)}
                  className="w-14 bg-surface border border-hair rounded px-2 py-1 text-sm text-onSurface text-right focus:outline-none focus:border-brand"
                />
                <span className="text-[11px] text-subtle">art.</span>
              </span>}
              {c.category === "Primo Piano" && (
                <span className="flex items-center gap-1" title="Ogni quanti minuti si ricalcolano le notizie in Primo Piano">
                  <input
                    type="number" min={min} max={max}
                    data-testid="recalc-input-primo-piano"
                    value={recalc ?? ""}
                    onChange={(e) => setRecalc(e.target.value)}
                    className="w-14 bg-surface border border-hair rounded px-2 py-1 text-sm text-onSurface text-right focus:outline-none focus:border-brand"
                  />
                  <span className="text-[11px] text-subtle">ricalc.</span>
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const HEALTH_STYLE = {
  error: { cls: "bg-bad/10 text-bad border-bad/40", label: "Errore" },
  stale: { cls: "bg-warn/10 text-warn border-warn/40", label: "Ferma" },
};

function SourceHealth({ s }) {
  const h = s.health;
  if (!h || h.status === "ok" || !HEALTH_STYLE[h.status]) return null;
  const st = HEALTH_STYLE[h.status];
  const since = s.last_ok_at ? ` · ultimo ok ${new Date(s.last_ok_at).toLocaleDateString("it-IT")}` : "";
  return (
    <span data-testid={`source-health-${s.source_id}`} title={h.message}
      className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${st.cls}`}>
      <AlertTriangle size={12} /> {st.label}: {h.message}{since}
    </span>
  );
}

const hasProblem = (s) => s.enabled !== false && ["error", "stale"].includes(s.health?.status);

function SourcesTab({ sources, reload }) {
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ name: "", url: "", category: "Cronaca" });
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const problems = sources.filter(hasProblem).length;
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const srcCat = (s) => (s.calcio ? "Calcio" : s.category);
  const cats = Array.from(new Set(sources.map(srcCat).filter(Boolean))).sort();
  const catOptions = Array.from(new Set([
    ...Object.keys(CATEGORY_COLOR).filter((c) => !["Oroscopo", "Ultimissime"].includes(c)),
    ...cats,
  ]));
  const filtered = sources.filter((s) => {
    const t = search.trim().toLowerCase();
    return (!t || (s.name || "").toLowerCase().includes(t)) && (catFilter === "all" || srcCat(s) === catFilter) && (!onlyProblems || hasProblem(s));
  });
  useEffect(() => { setPage(1); }, [search, catFilter, pageSize, onlyProblems]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const changeCategory = async (s, category) => {
    setBusy(`cat-${s.source_id}`);
    try { await updateSourceCategory(s.source_id, category); toast.success(`${s.name}: ora in ${displayCat(category)} (vale per i prossimi articoli)`); await reload(); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(""); }
  };

  const addSource = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) { toast.error("Nome e URL obbligatori"); return; }
    setBusy("add"); try { await createSource(form); setForm({ name: "", url: "", category: "Cronaca" }); toast.success("Fonte aggiunta"); await reload(); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(""); }
  };
  const doSync = async (id) => { setBusy(`s${id}`); try { const r = await syncSource(id); toast.success(`Sincronizzati ${r.ingested} articoli`); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(""); } };
  const doSyncAll = async () => { setBusy("all"); try { const r = await syncAllSources(); toast.success(`Sincronizzati ${r.ingested} articoli`); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(""); } };
  const genHoroscope = async () => { setBusy("horo"); try { await adminGenerateHoroscope(true); toast.success("Oroscopo generato per tutti i 12 segni"); } catch (e) { toast.error(e.message); } finally { setBusy(""); } };
  const rm = async (id) => { setBusy(`d${id}`); try { await deleteSource(id); toast.success("Fonte eliminata"); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(""); } };

  return (
    <Card>
      <CategoryRefreshPanel />
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <button data-testid="generate-horoscope-button" onClick={genHoroscope} disabled={busy === "horo"}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surfaceTertiary text-onSurface border border-hair text-xs font-semibold hover:border-brand/50 transition-colors">
          {busy === "horo" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} className="text-brand" />} Genera oroscopo (12 segni)
        </button>
        <button data-testid="sync-all-button" onClick={doSyncAll} disabled={busy === "all"}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand text-onBrand text-xs font-semibold hover:bg-brandSecondary transition-colors">
          {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sincronizza tutte
        </button>
      </div>
      <form onSubmit={addSource} className="flex flex-wrap items-center gap-2 mb-4">
        <input data-testid="source-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome" className="flex-1 min-w-[160px] bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        <input data-testid="source-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="URL feed RSS" className="flex-[2] min-w-[240px] bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        <select data-testid="source-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-40 bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
          {catOptions.map((c) => <option key={c} value={c}>{displayCat(c)}</option>)}
        </select>
        <button data-testid="add-source-button" type="submit" disabled={busy === "add"} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors">
          {busy === "add" ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Aggiungi
        </button>
      </form>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle" />
          <input data-testid="source-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome"
            className="w-full bg-surface border border-hair rounded-full pl-9 pr-4 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        </div>
        <select data-testid="source-category-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="bg-surface border border-hair rounded-full px-4 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
          <option value="all">Tutte le categorie</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="button" data-testid="source-problems-filter" onClick={() => setOnlyProblems((v) => !v)} disabled={!problems && !onlyProblems}
          className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border text-sm font-semibold transition-colors disabled:opacity-50 ${onlyProblems ? "bg-bad text-white border-bad" : "border-bad/40 text-bad hover:bg-bad/10"}`}>
          <AlertTriangle size={14} /> Con problemi ({problems})
        </button>
        <PageSizeSelect value={pageSize} onChange={setPageSize} testid="source-page-size" />
      </div>
      <div className="divide-y divide-hair/60">
        {paged.length === 0 && <p className="text-sm text-subtle py-3">Nessuna fonte trovata.</p>}
        {paged.map((s) => (
          <div key={s.source_id} data-testid={`source-row-${s.source_id}`} className="flex flex-wrap items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1 basis-[240px]">
              <p className="text-sm font-medium text-onSurface break-words">{s.name}</p>
              <select data-testid={`source-category-${s.source_id}`} value={srcCat(s)} disabled={busy === `cat-${s.source_id}`}
                onChange={(e) => changeCategory(s, e.target.value)} aria-label="Categoria della fonte"
                className="mt-1 bg-surface border border-hair rounded-full px-2.5 py-0.5 text-xs text-onSurfaceSecondary focus:outline-none focus:border-brand">
                {Array.from(new Set([...catOptions, srcCat(s)])).map((c) => <option key={c} value={c}>{displayCat(c)}</option>)}
              </select>
              <p className="text-xs text-subtle break-all">{s.url}</p>
              {s.enabled !== false && <SourceHealth s={s} />}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => doSync(s.source_id)} disabled={busy === `s${s.source_id}`} data-testid={`sync-source-${s.source_id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-hair text-xs text-onSurfaceSecondary hover:text-brand hover:border-brand/50 transition-colors">
                {busy === `s${s.source_id}` ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Sync
              </button>
              <button onClick={() => rm(s.source_id)} disabled={busy === `d${s.source_id}`} data-testid={`delete-source-${s.source_id}`}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-hair text-bad hover:border-bad/50 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      <Pager page={page} totalPages={totalPages} total={filtered.length} prefix="source"
        onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} />
    </Card>
  );
}

/* ---------------- Premi ---------------- */
function RewardsTab() {
  const [rewards, setRewards] = useState([]);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ title: "", points_cost: "", value_eur: "", stock: "999", image: "" });
  const load = useCallback(() => adminRewards().then((r) => setRewards(r.rewards || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.points_cost) { toast.error("Titolo e costo in Readoo obbligatori"); return; }
    setBusy("add");
    try {
      await adminCreateReward({ title: form.title.trim(), points_cost: Number(form.points_cost), value_eur: Number(form.value_eur || 0), stock: Number(form.stock || 0), image: form.image.trim() || null, active: true });
      setForm({ title: "", points_cost: "", value_eur: "", stock: "999", image: "" });
      toast.success("Premio creato"); await load();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(""); }
  };
  const patch = async (id, updates) => {
    setBusy(id);
    try { await adminUpdateReward(id, updates); await load(); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(""); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questo premio?")) return;
    setBusy(id);
    try { await adminDeleteReward(id); toast.success("Premio eliminato"); await load(); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(""); }
  };

  return (
    <Card>
      <form onSubmit={create} className="flex flex-wrap items-center gap-2 mb-5">
        <input data-testid="reward-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titolo premio" className="flex-1 min-w-[200px] bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        <input data-testid="reward-points" type="number" value={form.points_cost} onChange={(e) => setForm({ ...form, points_cost: e.target.value })} placeholder="Readoo" className="w-28 bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        <input data-testid="reward-value" type="number" value={form.value_eur} onChange={(e) => setForm({ ...form, value_eur: e.target.value })} placeholder="Valore €" className="w-28 bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        <input data-testid="reward-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock" className="w-24 bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand" />
        <button data-testid="add-reward-button" type="submit" disabled={busy === "add"} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors">
          {busy === "add" ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crea premio
        </button>
      </form>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-mono uppercase tracking-wider text-subtle border-b border-hair">
              <th className="py-2 pr-3 font-medium">Premio</th>
              <th className="py-2 pr-3 font-medium">Readoo</th>
              <th className="py-2 pr-3 font-medium">Stock</th>
              <th className="py-2 pr-3 font-medium">Stato</th>
              <th className="py-2 pr-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hair/50">
            {rewards.map((r) => (
              <tr key={r.reward_id} data-testid={`reward-row-${r.reward_id}`}>
                <td className="py-2 pr-3 text-onSurface">{r.title} {r.value_eur ? <span className="text-xs text-subtle">· {r.value_eur}€</span> : null}</td>
                <td className="py-2 pr-3">
                  <input type="number" defaultValue={r.points_cost} data-testid={`reward-points-${r.reward_id}`}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== r.points_cost) patch(r.reward_id, { points_cost: v }); }}
                    className="w-24 bg-surface border border-hair rounded px-2 py-1 text-sm text-onSurface focus:outline-none focus:border-brand" />
                </td>
                <td className="py-2 pr-3">
                  <input type="number" defaultValue={r.stock} data-testid={`reward-stock-${r.reward_id}`}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== r.stock) patch(r.reward_id, { stock: v }); }}
                    className="w-20 bg-surface border border-hair rounded px-2 py-1 text-sm text-onSurface focus:outline-none focus:border-brand" />
                </td>
                <td className="py-2 pr-3">
                  <button data-testid={`reward-toggle-${r.reward_id}`} disabled={busy === r.reward_id} onClick={() => patch(r.reward_id, { active: r.active === false })}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${r.active === false ? "bg-surfaceTertiary text-subtle" : "bg-brandTertiary text-brand"}`}>
                    <Power size={12} /> {r.active === false ? "Disattivo" : "Attivo"}
                  </button>
                </td>
                <td className="py-2 pr-0 text-right">
                  <button onClick={() => del(r.reward_id)} disabled={busy === r.reward_id} data-testid={`reward-delete-${r.reward_id}`}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-hair text-bad hover:border-bad/50 transition-colors">
                    {busy === r.reward_id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rewards.length === 0 && <p className="text-sm text-subtle py-4">Nessun premio configurato.</p>}
      </div>
    </Card>
  );
}

/* ---------------- Riscatti ---------------- */
function RedemptionsTab({ redemptions, reload }) {
  const [busy, setBusy] = useState("");
  const fulfill = async (id) => { setBusy(id); try { const r = await adminFulfill(id); toast.success(`Evaso · codice ${r.voucher_code}`); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(""); } };
  return (
    <Card>
      <div className="divide-y divide-hair/60">
        {redemptions.length === 0 && <p className="text-sm text-subtle py-3">Nessun riscatto.</p>}
        {redemptions.map((r) => (
          <div key={r.redemption_id} data-testid={`redemption-row-${r.redemption_id}`} className="flex flex-wrap items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1 basis-[240px]">
              <p className="text-sm font-medium text-onSurface break-words">{r.reward_title || r.reward_id}</p>
              <p className="text-xs text-subtle break-words">{r.user_email || r.user_id} · {r.status}{r.voucher_code ? ` · ${r.voucher_code}` : ""}</p>
            </div>
            {r.status === "pending" ? (
              <button onClick={() => fulfill(r.redemption_id)} disabled={busy === r.redemption_id} data-testid={`fulfill-${r.redemption_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand text-onBrand text-xs font-semibold hover:bg-brandSecondary transition-colors">
                {busy === r.redemption_id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Evadi
              </button>
            ) : (<span className="inline-flex items-center gap-1 text-xs text-ok"><Check size={13} /> Evaso</span>)}
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ---------------- Impostazioni ---------------- */
function SettingsTab() {
  const { gamificationEnabled, setGamificationEnabled, loginEnabled, setLoginEnabled } = useSettings();
  const [busy, setBusy] = useState("");
  const apply = (res) => { setLoginEnabled(res.login_enabled !== false); if ("gamification_enabled" in res) setGamificationEnabled(res.gamification_enabled !== false); };
  const toggleLogin = async () => {
    setBusy("login");
    try { const res = await adminSetLogin(!loginEnabled); apply(res); toast.success(res.login_enabled ? "Login attivato (livello 1)" : "Login disattivato: accesso solo admin"); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const toggleGami = async () => {
    setBusy("gami");
    try { const res = await adminSetGamification(!gamificationEnabled); setGamificationEnabled(res.gamification_enabled !== false); toast.success(res.gamification_enabled ? "Gamification attivata (livello 2)" : "Gamification disattivata"); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-6">
      <Card>
        <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-onSurface mb-4"><Power size={18} className="text-brand" /> Livelli di accesso</h3>
        <div className="space-y-5">
          <AccessRow
            testid="login-toggle" on={loginEnabled} busy={busy === "login"} onClick={toggleLogin}
            title="Login · livello 1"
            text={<>Visitatori possono <b>registrarsi e accedere</b> (email o Google): profilo, interessi, “Per te”, oroscopo e coupon. Readoo, premi, quiz e push restano spenti. Se spento: accedono solo gli admin e si spegne anche la gamification.</>}
          />
          <div className="border-t border-hair" />
          <AccessRow
            testid="gamification-toggle" on={gamificationEnabled} busy={busy === "gami"} onClick={toggleGami} disabled={!loginEnabled}
            title="Gamification · livello 2"
            text={!loginEnabled ? "Attiva prima il Login per poter accendere la gamification." : <>Aggiunge al livello 1 tutto il guadagno: <b>Readoo, livelli, premi, timer di lettura, quiz, convalida e notifiche push</b>.</>}
          />
        </div>
      </Card>
      <BrevoCard />
      <AskWebCard />
      <OpenAiCard />
      <GenerationCard />
    </div>
  );
}

function BrevoCard() {
  const [st, setSt] = useState(null);
  const [key, setKey] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { adminGetBrevo().then(setSt).catch(() => {}); }, []);
  const saveKey = async () => {
    setBusy("key");
    try { setSt(await adminSetBrevoKey(key.trim())); setKey(""); toast.success("Chiave Brevo salvata (cifrata)"); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { await adminTestBrevo(to.trim()); toast.success("Email di prova inviata: controlla la casella"); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const row = "flex justify-between gap-3 text-xs py-1 border-b border-hair/60";
  const input = "flex-1 min-w-0 bg-surface border border-hair rounded-full px-4 py-2 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand";
  const btn = "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-xs font-semibold hover:bg-brandSecondary disabled:opacity-50 transition-colors";
  return (
    <Card>
      <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-onSurface mb-4"><KeyRound size={18} className="text-brand" /> Email transazionali · Brevo</h3>
      {st && (
        <div data-testid="brevo-status" className="mb-4">
          <div className={row}><span className="text-subtle">Chiave API</span><span className="font-mono">{st.env_key ? "da Secrets (ambiente)" : st.stored_key ? "salvata qui (cifrata)" : "mancante"}</span></div>
          <div className={row}><span className="text-subtle">Mittente</span><span className="font-mono">{st.sender_name || "—"} &lt;{st.sender_email || "—"}&gt;</span></div>
          <div className={row}><span className="text-subtle">Rispondi a</span><span className="font-mono">{st.reply_to || "—"}</span></div>
          <div className={row}><span className="text-subtle">URL link nelle email</span><span className="font-mono truncate">{st.public_site_url || "—"}</span></div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Incolla qui la chiave API Brevo (xkeysib-…)" data-testid="brevo-key-input" className={input} />
        <button onClick={saveKey} disabled={busy === "key" || !key.trim()} data-testid="brevo-key-save" className={btn}>Salva chiave</button>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Email per l'invio di prova" data-testid="brevo-test-to" className={input} />
        <button onClick={test} disabled={busy === "test" || !to.trim()} data-testid="brevo-test-send" className={btn}>Invia prova</button>
      </div>
      <p className="text-[11px] text-subtle mt-3">In produzione la chiave va nei Secrets come BREVO_API_KEY (ha la priorità). La chiave non viene mai mostrata né inviata al sito.</p>
    </Card>
  );
}

function AskWebCard() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminGetAskWeb().then((r) => setOn(!!r.enabled)).catch(() => {}); }, []);
  const toggle = async () => {
    setBusy(true);
    try { const r = await adminSetAskWeb(!on); setOn(r.enabled); toast.success(r.enabled ? "Ricerca web attiva per tutti" : "Ricerca web solo superadmin"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card>
      <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-onSurface mb-4"><Sparkles size={18} className="text-brand" /> Chiedi a Readoo · ricerca web</h3>
      <AccessRow
        testid="ask-web-toggle" on={on} busy={busy} onClick={toggle}
        title="Info aggiornate dal web per tutti"
        text={<>Quando una domanda richiede notizie recenti, l’AI cerca online (OpenAI web search con la chiave OpenAI salvata qui sotto) e risponde con dati aggiornati, senza link esterni. Costo indicativo 1–3 cent. a domanda con ricerca. <b>Il superadmin la usa sempre</b>, anche se spenta.</>}
      />
    </Card>
  );
}

function AccessRow({ testid, on, busy, onClick, disabled, title, text }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="max-w-xl">
        <p className="text-sm font-semibold text-onSurface mb-1">{title}</p>
        <p data-testid={`${testid}-help`} className="text-sm text-onSurfaceSecondary">{text}</p>
      </div>
      <button data-testid={testid} role="switch" aria-checked={on} onClick={onClick} disabled={busy || disabled}
        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${on ? "bg-brand text-onBrand hover:bg-brandSecondary" : "bg-surfaceTertiary text-onSurface border border-hair hover:border-brand/50"}`}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Power size={16} />}
        {on ? "Attivo — clicca per spegnere" : "Spento — clicca per accendere"}
      </button>
    </div>
  );
}

function GenerationCard() {
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminGetGeneration().then((r) => setPaused(!!r.paused)).catch(() => {}); }, []);
  const toggle = async () => {
    setBusy(true);
    try {
      const res = await adminSetGeneration(!paused);
      setPaused(!!res.paused);
      toast.success(res.paused ? "Generazione in pausa (nessun token AI consumato)" : "Generazione riattivata");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card>
      <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-onSurface mb-2"><Power size={18} className="text-brand" /> Generazione contenuti (AI)</h3>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-onSurfaceSecondary max-w-xl">
          Quando è <b>in pausa</b>: si fermano l'importazione RSS e l'elaborazione AI degli articoli — <b>nessun token AI viene consumato</b>. Utile in staging o durante la manutenzione. In produzione tienila <b>attiva</b>.
        </p>
        <button data-testid="generation-toggle" role="switch" aria-checked={paused} onClick={toggle} disabled={busy}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors shrink-0 ${paused ? "bg-surfaceTertiary text-onSurface border border-hair hover:border-brand/50" : "bg-brand text-onBrand hover:bg-brandSecondary"}`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Power size={16} />}
          {paused ? "In pausa — clicca per riattivare" : "Attiva — clicca per mettere in pausa"}
        </button>
      </div>
    </Card>
  );
}

function OpenAiCard() {
  const [cfg, setCfg] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [useOpenai, setUseOpenai] = useState(false);
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => adminGetLlm().then((r) => { setCfg(r); setUseOpenai(r.use_openai); setModel(r.model || r.default_model || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const body = { use_openai: useOpenai, model: model.trim() };
      if (keyInput.trim()) body.openai_key = keyInput.trim();
      const res = await adminSetLlm(body);
      setKeyInput("");
      if (res && res.warning) toast.warning(res.warning, { duration: 8000 });
      else toast.success("Impostazioni LLM salvate");
      await load();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <Card>
      <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-onSurface mb-2"><KeyRound size={18} className="text-brand" /> Modello LLM & Chiave OpenAI</h3>
      <p className="text-sm text-onSurfaceSecondary mb-4 max-w-2xl">
        Il <b>modello</b> scelto qui viene usato per tutta l'app (elaborazione articoli, Ask Readoo, quiz, oroscopo). Se la chiave OpenAI è <b>attiva</b> e inserita, l'app usa la tua chiave con quel modello. Se spenta o senza chiave, usa la Universal Key di Emergent. La chiave è cifrata e non è mai visibile o recuperabile.
      </p>
      <div className="space-y-2 mb-5">
        <label className="text-xs font-mono uppercase tracking-wider text-subtle">Modello LLM</label>
        <input
          data-testid="llm-model-input" type="text" autoComplete="off"
          value={model} onChange={(e) => setModel(e.target.value)}
          placeholder={cfg.default_model}
          className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface font-mono focus:outline-none focus:border-brand"
        />
        <p className="text-[11px] text-subtle">Scrivi a mano il nome del modello (es. <code>{cfg.default_model}</code>). Aggiornalo quando escono nuovi modelli. Lascia vuoto per usare il default.</p>
      </div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <span className="text-sm text-onSurface">Usa la mia chiave OpenAI</span>
        <button data-testid="llm-toggle" role="switch" aria-checked={useOpenai} onClick={() => setUseOpenai((v) => !v)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-colors ${useOpenai ? "bg-brand text-onBrand hover:bg-brandSecondary" : "bg-surfaceTertiary text-onSurface border border-hair hover:border-brand/50"}`}>
          <Power size={15} /> {useOpenai ? "Attiva" : "Spenta"}
        </button>
      </div>
      <div className="space-y-2">
        <label className="text-xs font-mono uppercase tracking-wider text-subtle">Chiave OpenAI</label>
        <input
          data-testid="llm-key-input" type="password" autoComplete="off"
          value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
          placeholder={cfg.configured ? `Chiave impostata (${cfg.masked}) — inserisci per sostituire` : "sk-..."}
          className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand"
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-subtle">{cfg.configured ? "Una chiave è già salvata e cifrata. Lascia vuoto per non modificarla." : "Nessuna chiave salvata: verrà usata la Universal Key."}</p>
          <button onClick={save} disabled={busy} data-testid="llm-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Salva
          </button>
        </div>
      </div>
    </Card>
  );
}

/* ---------------- Pubblicità (Ad Inserter) ---------------- */
const AD_PLACEMENTS = [
  { v: "head", l: "Head (dentro <head>)" },
  { v: "footer", l: "Footer (fine <body>)" },
  { v: "before_post", l: "Prima dell'articolo" },
  { v: "before_content", l: "Prima del contenuto" },
  { v: "before_paragraph", l: "Prima dell'N-esimo paragrafo" },
  { v: "after_paragraph", l: "Dopo l'N-esimo paragrafo" },
  { v: "before_image", l: "Prima dell'immagine" },
  { v: "after_image", l: "Dopo l'immagine" },
  { v: "after_content", l: "Dopo il contenuto" },
  { v: "between_posts", l: "Tra i post (liste)" },
  { v: "home_after_featured", l: "Home — dopo gli articoli in evidenza" },
  { v: "before_html_element", l: "Prima di un elemento (selettore CSS)" },
  { v: "inside_html_element", l: "Dentro un elemento (selettore CSS)" },
  { v: "after_html_element", l: "Dopo un elemento (selettore CSS)" },
];
const AD_TARGETING = [
  { v: "all", l: "Tutte le pagine" },
  { v: "articles", l: "Solo articoli" },
  { v: "home", l: "Solo home" },
  { v: "category", l: "Categoria specifica" },
];
const AD_CATEGORIES = Object.keys(CATEGORY_COLOR);
const isParagraphPlacement = (p) => p === "before_paragraph" || p === "after_paragraph";
const isSelectorPlacement = (p) => ["before_html_element", "inside_html_element", "after_html_element"].includes(p);
const emptyAdForm = () => ({
  name: "", code: "", placement: "before_content", paragraph: 2, selector: "",
  targeting: "all", category: null, enabled: true, show_label: false, require_consent: false, priority: 100,
  zones: { frontend: true, profilo: false, admin: false },
});

function AdsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=nessuno; {} form aperto
  const [form, setForm] = useState(emptyAdForm());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminAdblocks().then((r) => setItems(r.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyAdForm()); setEditing("new"); };
  const openEdit = (b) => { setForm({ ...emptyAdForm(), ...b }); setEditing(b.id); };
  const close = () => { setEditing(null); setForm(emptyAdForm()); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci un nome"); return; }
    if (!form.code.trim()) { toast.error("Inserisci il codice del blocco"); return; }
    if (isSelectorPlacement(form.placement) && !form.selector.trim()) { toast.error("Inserisci un selettore CSS"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        paragraph: Number(form.paragraph) || 1,
        priority: Number(form.priority) ?? 100,
        category: form.targeting === "category" ? form.category : null,
      };
      if (editing === "new") await adminCreateAdblock(payload);
      else await adminUpdateAdblock(editing, payload);
      toast.success("Blocco salvato");
      close();
      load();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo blocco pubblicitario?")) return;
    try { await adminDeleteAdblock(id); toast.success("Blocco eliminato"); load(); }
    catch (e) { toast.error(e.message || "Errore"); }
  };

  const toggleEnabled = async (b) => {
    try { await adminUpdateAdblock(b.id, { ...b, enabled: !b.enabled }); load(); }
    catch (e) { toast.error(e.message || "Errore"); }
  };

  const placementLabel = (v) => AD_PLACEMENTS.find((p) => p.v === v)?.l || v;
  const targetLabel = (b) => b.targeting === "category" ? `Categoria: ${displayCat(b.category || "—")}` : (AD_TARGETING.find((t) => t.v === b.targeting)?.l || b.targeting);

  return (
    <div className="space-y-6" data-testid="ads-tab">
      <Card>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-onSurface mb-1"><Megaphone size={18} className="text-brand" /> Ad Inserter — blocchi pubblicitari</h3>
            <p className="text-sm text-onSurfaceSecondary max-w-2xl">
              Crea blocchi di codice HTML/JS (adv, Google Analytics/GTM, Taboola, pixel, banner) e iniettali nel sito in posizioni configurabili. Il caricamento è differito per non penalizzare le performance. Per ogni blocco scegli le <strong>aree</strong> in cui attivarlo: frontend, profilo e/o admin. Gli asset non sono mai tracciati.
            </p>
          </div>
          <button onClick={openNew} data-testid="ad-new-button" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary shrink-0">
            <Plus size={15} /> Nuovo blocco
          </button>
        </div>
      </Card>

      {editing && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-base font-semibold text-onSurface">{editing === "new" ? "Nuovo blocco" : "Modifica blocco"}</h4>
            <button onClick={close} data-testid="ad-form-close" className="text-subtle hover:text-brand"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs font-mono uppercase tracking-wider text-subtle">Nome</label>
              <input data-testid="ad-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120}
                className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" placeholder="Es. GA4 Loader, Banner 300x250" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs font-mono uppercase tracking-wider text-subtle">Codice (HTML + JS)</label>
              <textarea data-testid="ad-code" value={form.code} onChange={(e) => set("code", e.target.value)} rows={7}
                className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-xs font-mono text-onSurface focus:outline-none focus:border-brand" placeholder="<script>...</script> oppure <ins class='adsbygoogle' ...></ins>" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-mono uppercase tracking-wider text-subtle">Posizione</label>
              <select data-testid="ad-placement" value={form.placement} onChange={(e) => set("placement", e.target.value)}
                className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
                {AD_PLACEMENTS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-mono uppercase tracking-wider text-subtle">Targeting</label>
              <select data-testid="ad-targeting" value={form.targeting} onChange={(e) => set("targeting", e.target.value)}
                className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
                {AD_TARGETING.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            {form.targeting === "category" && (
              <div className="space-y-1">
                <label className="text-xs font-mono uppercase tracking-wider text-subtle">Categoria</label>
                <select data-testid="ad-category" value={form.category || ""} onChange={(e) => set("category", e.target.value || null)}
                  className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
                  <option value="">— Seleziona —</option>
                  {AD_CATEGORIES.map((c) => <option key={c} value={c}>{displayCat(c)}</option>)}
                </select>
              </div>
            )}
            {isParagraphPlacement(form.placement) && (
              <div className="space-y-1">
                <label className="text-xs font-mono uppercase tracking-wider text-subtle">Numero paragrafo</label>
                <input data-testid="ad-paragraph" type="number" min={1} value={form.paragraph} onChange={(e) => set("paragraph", e.target.value)}
                  className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
              </div>
            )}
            {isSelectorPlacement(form.placement) && (
              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-mono uppercase tracking-wider text-subtle">Selettore CSS</label>
                <input data-testid="ad-selector" value={form.selector} onChange={(e) => set("selector", e.target.value)} maxLength={200}
                  className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm font-mono text-onSurface focus:outline-none focus:border-brand" placeholder='Es. [data-testid="article-body"]' />
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs font-mono uppercase tracking-wider text-subtle">Ordine di esecuzione (priorità)</label>
              <input data-testid="ad-priority" type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)}
                className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
              <p className="text-[11px] text-subtle">Più basso = eseguito prima.</p>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-hair bg-surfaceSecondary p-3">
            <p className="text-xs font-mono uppercase tracking-wider text-subtle mb-2">Attiva nelle aree</p>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-onSurface cursor-pointer">
                <input data-testid="ad-zone-frontend" type="checkbox" checked={!!form.zones?.frontend} onChange={(e) => set("zones", { ...form.zones, frontend: e.target.checked })} className="accent-brand" /> Frontend (sito pubblico)
              </label>
              <label className="flex items-center gap-2 text-sm text-onSurface cursor-pointer">
                <input data-testid="ad-zone-profilo" type="checkbox" checked={!!form.zones?.profilo} onChange={(e) => set("zones", { ...form.zones, profilo: e.target.checked })} className="accent-brand" /> Profilo (pagine utente)
              </label>
              <label className="flex items-center gap-2 text-sm text-onSurface cursor-pointer">
                <input data-testid="ad-zone-admin" type="checkbox" checked={!!form.zones?.admin} onChange={(e) => set("zones", { ...form.zones, admin: e.target.checked })} className="accent-brand" /> Admin (pannello)
              </label>
            </div>
            <p className="text-[11px] text-subtle mt-2">Profilo = /profilo, /livelli, /missioni, /premi, /il-mio-segno, ecc. Se non selezioni nulla, il blocco non verrà mai iniettato.</p>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm text-onSurface cursor-pointer">
              <input data-testid="ad-enabled" type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} className="accent-brand" /> Attivo
            </label>
            <label className="flex items-center gap-2 text-sm text-onSurface cursor-pointer">
              <input data-testid="ad-show-label" type="checkbox" checked={form.show_label} onChange={(e) => set("show_label", e.target.checked)} className="accent-brand" /> Mostra etichetta "Pubblicità"
            </label>
            <label className="flex items-center gap-2 text-sm text-onSurface cursor-pointer">
              <input data-testid="ad-require-consent" type="checkbox" checked={form.require_consent} onChange={(e) => set("require_consent", e.target.checked)} className="accent-brand" /> Carica solo dopo consenso (CMP)
            </label>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={close} className="px-4 py-2 rounded-full border border-hair text-sm text-onSurfaceSecondary hover:text-brand hover:border-brand/50">Annulla</button>
            <button onClick={save} disabled={busy} data-testid="ad-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Salva
            </button>
          </div>
        </Card>
      )}

      <Card>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-subtle py-3" data-testid="ads-empty">Nessun blocco pubblicitario. Creane uno con "Nuovo blocco".</p>
        ) : (
          <div className="divide-y divide-hair/60">
            {items.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 py-3" data-testid={`ad-row-${b.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-onSurface truncate">{b.name || "(senza nome)"}
                    {b.require_consent && <span className="ml-2 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-surfaceTertiary text-subtle">consenso</span>}
                  </p>
                  <p className="text-xs text-subtle truncate">{placementLabel(b.placement)} · {targetLabel(b)} · priorità {b.priority}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`ad-toggle-${b.id}`} onClick={() => toggleEnabled(b)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${b.enabled ? "bg-brandTertiary text-brand" : "bg-surfaceTertiary text-subtle"}`}>
                    <Power size={12} /> {b.enabled ? "Attivo" : "Disattivo"}
                  </button>
                  <button data-testid={`ad-edit-${b.id}`} onClick={() => openEdit(b)} className="p-1.5 rounded-lg text-onSurfaceSecondary hover:text-brand hover:bg-surfaceSecondary"><Settings size={15} /></button>
                  <button data-testid={`ad-delete-${b.id}`} onClick={() => del(b.id)} className="p-1.5 rounded-lg text-onSurfaceSecondary hover:text-red-400 hover:bg-surfaceSecondary"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}


/* ---------------- Notifiche push ---------------- */
const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function RecurringCard({ rec, reload }) {
  const [f, setF] = useState({ ...rec });
  const [busy, setBusy] = useState(false);
  const toggleDay = (d) => setF((p) => ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d].sort() }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { title: f.title, body: f.body, url: f.url || "/", time: f.time, days: f.days, audience: f.audience, active: f.active };
      if (rec.push_id) await adminUpdateRecurring(rec.push_id, body); else await adminCreateRecurring(body);
      toast.success("Salvato"); await reload();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  const del = async () => { if (!window.confirm("Eliminare questa push ricorrente?")) return; try { await adminDeleteRecurring(rec.push_id); toast.success("Eliminata"); await reload(); } catch (e) { toast.error(e.message); } };
  return (
    <div data-testid={`recurring-card-${rec.push_id || "new"}`} className="rounded-xl border border-hair bg-surface p-4 space-y-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titolo" className="flex-1 min-w-[160px] bg-surfaceSecondary border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
        <input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className="bg-surfaceSecondary border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
        <label className="flex items-center gap-1.5 text-xs text-onSurfaceSecondary"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Attiva</label>
      </div>
      <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Testo della notifica" rows={2} className="w-full bg-surfaceSecondary border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
      <div className="flex items-center gap-2 flex-wrap">
        <input value={f.url || ""} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="Link (es. /profilo)" className="flex-1 min-w-[140px] bg-surfaceSecondary border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
        <select value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} className="bg-surfaceSecondary border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand">
          <option value="all">Tutti gli iscritti</option>
          <option value="pending">Solo con Readoo da convalidare</option>
        </select>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {DAY_LABELS.map((lbl, i) => (
          <button key={i} type="button" onClick={() => toggleDay(i)} data-testid={`rec-day-${i}`}
            className={`w-9 h-8 rounded-md text-xs font-medium transition-colors ${f.days.includes(i) ? "bg-brand text-onBrand" : "bg-surfaceSecondary text-subtle border border-hair"}`}>{lbl}</button>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2">
        {rec.push_id && <button onClick={del} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-hair text-bad text-xs hover:border-bad/50"><Trash2 size={13} /> Elimina</button>}
        <button onClick={save} disabled={busy} data-testid="save-recurring" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand text-onBrand text-xs font-semibold hover:bg-brandSecondary disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salva
        </button>
      </div>
    </div>
  );
}

function PushTab() {
  const [data, setData] = useState(null);
  const [manual, setManual] = useState({ title: "", body: "", url: "/", send_at: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminPushOverview().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!manual.title.trim() || !manual.body.trim()) { toast.error("Titolo e testo obbligatori"); return; }
    setBusy(true);
    try {
      const body = { title: manual.title.trim(), body: manual.body.trim(), url: manual.url.trim() || "/" };
      if (manual.send_at) body.send_at = new Date(manual.send_at).toISOString();
      const r = await adminPushSend(body);
      toast.success(r.scheduled ? "Push pianificata" : `Inviata a ${r.sent} iscritti`);
      setManual({ title: "", body: "", url: "/", send_at: "" });
      await load();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  const addRecurring = () => setData((d) => ({ ...d, recurring: [...d.recurring, { title: "", body: "", url: "/", time: "09:00", days: [0, 1, 2, 3, 4, 5, 6], audience: "all", active: true }] }));
  const cancelScheduled = async (id) => {
    if (!window.confirm("Annullare questa push pianificata?")) return;
    try { await adminDeleteScheduled(id); toast.success("Push pianificata annullata"); await load(); }
    catch (e) { toast.error(e.message || "Errore"); }
  };

  if (!data) return <Card><div className="py-8 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div></Card>;

  return (
    <div className="space-y-6" data-testid="push-tab">
      <Card>
        <div className="flex items-center gap-2 mb-1"><Bell size={17} className="text-brand" /><h3 className="font-serif text-lg font-semibold text-onSurface">Iscritti alle notifiche</h3></div>
        <p className="text-sm text-subtle">Dispositivi iscritti: <b className="text-onSurface" data-testid="push-subscribers">{data.subscribers}</b></p>
      </Card>

      <SoftAskEditor softask={data.softask} reload={load} />

      <Card>
        <div className="flex items-center gap-2 mb-3"><Send size={17} className="text-brand" /><h3 className="font-serif text-lg font-semibold text-onSurface">Push manuale (breaking)</h3></div>
        <div className="space-y-2.5">
          <input data-testid="manual-title" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} placeholder="Titolo" className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
          <textarea data-testid="manual-body" value={manual.body} onChange={(e) => setManual({ ...manual, body: e.target.value })} placeholder="Testo" rows={2} className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
          <div className="flex items-center gap-2 flex-wrap">
            <input value={manual.url} onChange={(e) => setManual({ ...manual, url: e.target.value })} placeholder="Link (es. /cronaca/...)" className="flex-1 min-w-[160px] bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
            <label className="text-xs text-subtle flex items-center gap-1.5">Pianifica: <input type="datetime-local" data-testid="manual-schedule" value={manual.send_at} onChange={(e) => setManual({ ...manual, send_at: e.target.value })} className="bg-surface border border-hair rounded-lg px-2 py-1.5 text-sm text-onSurface focus:outline-none focus:border-brand" /></label>
            <button onClick={send} disabled={busy} data-testid="manual-send" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {manual.send_at ? "Pianifica" : "Invia ora"}
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-3"><Clock size={17} className="text-brand" /><h3 className="font-serif text-lg font-semibold text-onSurface">Push pianificate</h3></div>
        <div className="space-y-2" data-testid="scheduled-list">
          {data.scheduled.length === 0 && <p className="text-sm text-subtle">Nessuna push pianificata.</p>}
          {data.scheduled.map((s) => (
            <div key={s.push_id} data-testid={`scheduled-${s.push_id}`} className="flex items-start justify-between gap-3 rounded-lg border border-hair bg-surface p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-onSurface truncate">{s.title}</p>
                <p className="text-xs text-onSurfaceSecondary line-clamp-2">{s.body}</p>
                <p className="text-[11px] text-brand font-mono mt-0.5">Invio: {new Date(s.send_at).toLocaleString("it-IT")}</p>
              </div>
              <button onClick={() => cancelScheduled(s.push_id)} data-testid={`cancel-scheduled-${s.push_id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-hair text-bad text-xs hover:border-bad/50 shrink-0"><Trash2 size={13} /> Annulla</button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2"><Clock size={17} className="text-brand" /><h3 className="font-serif text-lg font-semibold text-onSurface">Push ricorrenti (orario italiano)</h3></div>
          <button onClick={addRecurring} data-testid="add-recurring" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand text-onBrand text-xs font-semibold hover:bg-brandSecondary"><Plus size={14} /> Aggiungi</button>
        </div>
        <div className="space-y-3">
          {data.recurring.map((r, i) => <RecurringCard key={r.push_id || `new-${i}`} rec={r} reload={load} />)}
          {data.recurring.length === 0 && <p className="text-sm text-subtle">Nessuna push ricorrente.</p>}
        </div>
      </Card>
    </div>
  );
}

function SoftAskEditor({ softask, reload }) {
  const [f, setF] = useState(softask || { title: "", body: "", cta: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await adminSetSoftAsk({ title: f.title, body: f.body, cta: f.cta }); toast.success("Testo aggiornato"); await reload(); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3"><Bell size={17} className="text-brand" /><h3 className="font-serif text-lg font-semibold text-onSurface">Banner "Attiva notifiche" (testo)</h3></div>
      <div className="space-y-2.5">
        <input data-testid="softask-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titolo" className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
        <textarea data-testid="softask-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Testo" rows={2} className="w-full bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="softask-cta" value={f.cta} onChange={(e) => setF({ ...f, cta: e.target.value })} placeholder="Testo pulsante" className="w-48 bg-surface border border-hair rounded-lg px-3 py-2 text-sm text-onSurface focus:outline-none focus:border-brand" />
          <button onClick={save} disabled={busy} data-testid="save-softask" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Salva testo
          </button>
        </div>
      </div>
    </Card>
  );
}

function LevelsTab() {
  const [levels, setLevels] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminGetLevels().then((r) => setLevels(r.levels)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const setField = (i, k, v) => setLevels((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const save = async () => {
    setBusy(true);
    try {
      const payload = levels.map((l) => ({
        level_number: Number(l.level_number), name: l.name,
        min_validated_redoo: Number(l.min_validated_redoo), article_reward: Number(l.article_reward),
        maintenance_active_days: Number(l.maintenance_active_days), is_active: l.is_active !== false,
      }));
      await adminSetLevels(payload); toast.success("Livelli aggiornati"); await load();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  if (!levels) return <Card><div className="py-8 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div></Card>;
  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2"><Trophy size={17} className="text-brand" /><h3 className="font-serif text-lg font-semibold text-onSurface">Configurazione livelli</h3></div>
        <button onClick={save} disabled={busy} data-testid="save-levels" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Salva livelli
        </button>
      </div>
      <p className="text-xs text-subtle mb-3">Soglia = Readoo convalidati necessari. Reward = Readoo per articolo letto a quel livello. Mant. = giorni attivi richiesti su 30.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-subtle border-b border-hair">
            <th className="py-2 pr-2 font-medium">Lv</th>
            <th className="py-2 pr-2 font-medium">Nome</th>
            <th className="py-2 pr-2 font-medium">Soglia Readoo</th>
            <th className="py-2 pr-2 font-medium">Reward/articolo</th>
            <th className="py-2 pr-2 font-medium">Mant. (gg/30)</th>
            <th className="py-2 pr-2 font-medium">Attivo</th>
          </tr></thead>
          <tbody>
            {levels.map((l, i) => (
              <tr key={l.level_number} data-testid={`level-edit-${l.level_number}`} className="border-b border-hair/50">
                <td className="py-2 pr-2 font-mono text-onSurface">{l.level_number}</td>
                <td className="py-2 pr-2"><input value={l.name} onChange={(e) => setField(i, "name", e.target.value)} className="w-32 bg-surface border border-hair rounded px-2 py-1 text-onSurface focus:outline-none focus:border-brand" /></td>
                <td className="py-2 pr-2"><input type="number" value={l.min_validated_redoo} onChange={(e) => setField(i, "min_validated_redoo", e.target.value)} className="w-24 bg-surface border border-hair rounded px-2 py-1 text-onSurface focus:outline-none focus:border-brand" /></td>
                <td className="py-2 pr-2"><input type="number" value={l.article_reward} onChange={(e) => setField(i, "article_reward", e.target.value)} className="w-20 bg-surface border border-hair rounded px-2 py-1 text-onSurface focus:outline-none focus:border-brand" /></td>
                <td className="py-2 pr-2"><input type="number" value={l.maintenance_active_days} onChange={(e) => setField(i, "maintenance_active_days", e.target.value)} className="w-20 bg-surface border border-hair rounded px-2 py-1 text-onSurface focus:outline-none focus:border-brand" /></td>
                <td className="py-2 pr-2"><input type="checkbox" checked={l.is_active !== false} onChange={(e) => setField(i, "is_active", e.target.checked)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---------------- Page ---------------- */
export default function Admin() {
  const { user, loading } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const isAdminUser = ["admin", "superadmin"].includes(user?.role);
  const visibleTabs = TABS.filter((t) => !t.superadmin || isSuperadmin);
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sources, setSources] = useState([]);
  const [redemptions, setRedemptions] = useState([]);

  const loadStats = useCallback(() => adminStats().then(setStats).catch(() => {}), []);
  const loadUsers = useCallback(() => adminUsers().then((r) => setUsers(r.users || [])).catch(() => {}), []);
  const loadSources = useCallback(() => getSources().then((r) => setSources(r.sources || [])).catch(() => {}), []);
  const loadRedemptions = useCallback(() => adminRedemptions().then((r) => setRedemptions(r.redemptions || [])).catch(() => {}), []);

  useEffect(() => {
    if (!isAdminUser) return;
    loadStats(); loadUsers(); loadRedemptions();
    if (isSuperadmin) loadSources();
  }, [isAdminUser, isSuperadmin, loadStats, loadUsers, loadSources, loadRedemptions]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  if (!user || !isAdminUser) return <Navigate to="/login" replace />;

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-panel">
      <div className="flex items-center gap-2.5 mb-6">
        <ShieldCheck className="text-brand" size={26} />
        <h1 className="font-serif text-3xl font-semibold text-onSurface">Amministrazione</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="lg:sticky lg:top-24 self-start">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-1 -mx-1 px-1">
            {visibleTabs.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                  tab === id ? "bg-brand text-onBrand" : "text-onSurfaceSecondary hover:bg-surfaceSecondary hover:text-brand"
                }`}>
                <Icon size={17} /> {label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0" data-testid={`admin-content-${tab}`}>
          {tab === "dashboard" && <DashboardTab stats={stats} />}
          {tab === "utenti" && <UsersTab users={users} reload={() => { loadUsers(); loadStats(); }} />}
          {tab === "articoli" && <ArticlesTab />}
          {tab === "scrivi" && <ManualArticleTab />}
          {tab === "engagement" && <EngagementTab />}
          {tab === "fonti" && isSuperadmin && <SourcesTab sources={sources} reload={loadSources} />}
          {tab === "premi" && <RewardsTab />}
          {tab === "riscatti" && <RedemptionsTab redemptions={redemptions} reload={() => { loadRedemptions(); loadStats(); }} />}
          {tab === "notifiche" && <PushTab />}
          {tab === "pubblicita" && isSuperadmin && <AdsTab />}
          {tab === "livelli" && <LevelsTab />}
          {tab === "impostazioni" && isSuperadmin && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}
